import {type RumGlobal} from '@datadog/browser-rum';

import {isSystemMetric} from '../../../../util/metrics';
import {type RunsLinePlotConfig} from '../../types';

export function checkBackendLinesSupport({
  config,
  context: {datadogRum, panelId, usesBackendLineConstruction},
  usesBackendLineConstructionMultiMetric,
  usesBackendLineConstructionSmoothing,
  usesBackendLineConstructionSystemMetrics,
}: {
  config: RunsLinePlotConfig;
  context: {
    datadogRum: RumGlobal;
    panelId?: string;
    usesBackendLineConstruction: boolean;
  };
  usesBackendLineConstructionMultiMetric: boolean;
  usesBackendLineConstructionSmoothing: boolean;
  usesBackendLineConstructionSystemMetrics: boolean;
}) {
  const sharedContext = {
    panelId,
    usesBackendLineConstruction,
    usesBackendLineConstructionSystemMetrics,
    usesBackendLineConstructionMultiMetric,
    usesBackendLineConstructionSmoothing,
  };

  if (config.metrics && config.metrics.includes('_step')) {
    datadogRum.addAction('backend lines not supported', {
      // weird issue where this is hard to resolve in the Big Table V3 store
      // https://weightsandbiases.slack.com/archives/C07RYBTDQEQ/p1764972800037409
      reason: '_step key in metrics values',
      ...sharedContext,
    });
    return false;
  }

  if (config.metrics && config.xAxis && config.metrics.includes(config.xAxis)) {
    datadogRum.addAction('backend lines not supported', {
      reason: 'shared x and y axis metric',
      ...sharedContext,
    });
    return false;
  }

  if (
    !usesBackendLineConstructionSystemMetrics &&
    config.metrics &&
    config.metrics.some(isSystemMetric)
  ) {
    datadogRum.addAction('backend lines not supported', {
      reason: 'system metrics',
      ...sharedContext,
    });
    return false;
  }

  // multiple metrics require feature flag
  if (
    config.metrics &&
    config.metrics.length > 1 &&
    !usesBackendLineConstructionMultiMetric
  ) {
    datadogRum.addAction('backend lines not supported', {
      reason: 'multiple metrics',
      ...sharedContext,
    });
    return false;
  }

  if (config.expressions) {
    datadogRum.addAction('backend lines not supported', {
      reason: 'y-axis expressions',
      ...sharedContext,
    });
    return false;
  }

  if (config.xExpression) {
    datadogRum.addAction('backend lines not supported', {
      reason: 'x-axis expressions',
      ...sharedContext,
    });
    return false;
  }

  if (
    config.smoothingWeight &&
    config.smoothingWeight > 0 &&
    !usesBackendLineConstructionSmoothing
  ) {
    datadogRum.addAction('backend lines not supported', {
      reason: 'smoothing',
      ...sharedContext,
    });
    return false;
  }

  // log scales not yet supported
  if (config.xLogScale || config.yLogScale) {
    datadogRum.addAction('backend lines not supported', {
      reason: 'log scales',
      ...sharedContext,
    });
    return false;
  }

  // cannot exclude outliers yet
  if (config.excludeOutliers === 'exclude-outliers') {
    datadogRum.addAction('backend lines not supported', {
      reason: 'exclude outliers',
      ...sharedContext,
    });
    return false;
  }

  // cannot do backend lines for non-bucketing, should never be hit
  if (config.pointVisualizationMethod !== 'bucketing-gorilla') {
    datadogRum.addAction('backend lines not supported', {
      reason: 'non-bucketing visualization method',
      ...sharedContext,
    });
    return false;
  }

  return true;
}
